import { Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'flexyoo-hr-invite-employee-date-range',
  templateUrl: 'hr-invite-employee-date-range.component.html',
  styleUrls: ['./hr-invite-employee-date-range.component.css'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    MatDatepickerModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    FlexLayoutModule,
  ],
})
export class HrInviteEmployeeDateRangeComponent implements OnInit {
  toDate: string;
  saveFunc: (data: { fromDate: string; toDate: string }) => Promise<any>;
  private readonly dialogRef = inject(MatDialogRef<HrInviteEmployeeDateRangeComponent>);
  protected isInviteBlocked = false;
  protected inviteBlockedTooltipKey: string | null = null;

  constructor() {
    const dialogData = inject(MAT_DIALOG_DATA, { optional: true });
    if (dialogData) {
      this.toDate = dialogData.toDate;
      this.saveFunc = dialogData.saveFunc;
      this.isInviteBlocked = !!dialogData.isInviteBlocked;
      this.inviteBlockedTooltipKey = dialogData.inviteBlockedTooltipKey ?? null;
    }
  }

  dateRangeValidator: ValidatorFn = (formGroup: FormGroup): ValidationErrors | null => {
    const fromDate = formGroup.get('fromDate').value?.toString();
    const toDateVal = formGroup.get('toDate').value;
    const toDate = toDateVal?.toISOString ? toDateVal?.toISOString() : toDateVal;
    return fromDate && toDate && toDate < fromDate ? { dateRangeInvalid: true } : null;
  };

  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.group(
    {
      fromDate: this.fb.control(''),
      toDate: inject(FormBuilder).control(''),
    },
    { validators: this.dateRangeValidator }
  );

  fromDate = toSignal(this.form.controls.fromDate.valueChanges);

  minToDate = computed(() => {
    const date = new Date(this.fromDate());
    date.setDate(date.getDate() + 1);
    return date;
  });

  ngOnInit(): void {
    const defaultValue = {
      fromDate: new Date().toISOString(),
      toDate: this.toDate || '',
    };
    this.form.patchValue(defaultValue);
    this.form.controls.fromDate.disable();
  }

  async onSave() {
    if (this.isInviteBlocked) {
      return;
    }
    if (this.saveFunc) {
      await this.saveFunc(this.form.value as Required<typeof this.form.value>);
    }
    this.dialogRef.close(this.form.value.toDate);
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
